import { Loader2, ShieldCheck, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EhsrClause } from '../../ehsr/types'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'

type RiskReductionMeasureLinkedEhsrsSectionProps = {
  ehsrClauseIds: string[]
  clauseById: Map<string, EhsrClause>
  removingClauseId: string | null
  onRemoveClick: (clauseId: string) => void
  onAddClick: () => void
  isAddDisabled: boolean
}

function getClauseTitle(clauseId: string, clause?: EhsrClause) {
  return clause?.selectedLabel ?? clauseId
}

function getClauseDescription(clause?: EhsrClause) {
  if (clause?.label != null && clause.label.trim().length > 0) {
    return clause.label
  }
  return null
}

export function RiskReductionMeasureLinkedEhsrsSection({
  ehsrClauseIds,
  clauseById,
  removingClauseId,
  onRemoveClick,
  onAddClick,
  isAddDisabled,
}: RiskReductionMeasureLinkedEhsrsSectionProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasLinkedEhsrs = ehsrClauseIds.length > 0

  return (
    <section className='space-y-3'>
      <div className='flex items-center justify-between gap-2'>
        <h2 className='text-lg font-semibold tracking-tight'>
          {t('hazardsPage.hazardDetail.ehsr')}
        </h2>
        {hasLinkedEhsrs ? (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onAddClick}
            disabled={isAddDisabled}
          >
            {t('riskReductionMeasuresLibraryPage.rrmDetail.addEhsrAction')}
          </Button>
        ) : null}
      </div>
      {!hasLinkedEhsrs && (
        <Empty className='rounded-lg border border-dashed py-8'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ShieldCheck className='size-6' />
            </EmptyMedia>
            <EmptyTitle>
              {t(
                'riskReductionMeasuresLibraryPage.rrmDetail.linkedEhsrsEmptyTitle'
              )}
            </EmptyTitle>
            <EmptyDescription>
              {t(
                'riskReductionMeasuresLibraryPage.rrmDetail.linkedEhsrsEmptyDescription'
              )}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size='sm' onClick={onAddClick} disabled={isAddDisabled}>
              {t('riskReductionMeasuresLibraryPage.rrmDetail.addEhsrAction')}
            </Button>
          </EmptyContent>
        </Empty>
      )}
      {hasLinkedEhsrs && (
        <ul className='space-y-1'>
          {ehsrClauseIds.map((clauseId) => {
            const clause = clauseById.get(clauseId)
            const clauseTitle = getClauseTitle(clauseId, clause)
            const clauseDescription = getClauseDescription(clause)
            const isRemoving = removingClauseId === clauseId
            const showDescription =
              clauseDescription != null &&
              clauseDescription.trim().length > 0 &&
              clauseDescription !== clauseTitle

            return (
              <li key={clauseId}>
                <Item variant='outline' size='sm'>
                  <ItemContent className='min-w-0 flex-1'>
                    <ItemTitle className='cursor-default text-foreground'>
                      {clauseTitle}
                    </ItemTitle>
                    {showDescription ? (
                      <ItemDescription className='line-clamp-2 text-xs text-muted-foreground'>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <span className='block cursor-default'>
                              {clauseDescription}
                            </span>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side='top'
                            className='max-w-sm text-sm text-muted-foreground'
                          >
                            <p className='whitespace-pre-wrap'>
                              {clauseDescription}
                            </p>
                          </HoverCardContent>
                        </HoverCard>
                      </ItemDescription>
                    ) : null}
                  </ItemContent>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='shrink-0 text-muted-foreground hover:text-destructive'
                    onClick={() => onRemoveClick(clauseId)}
                    disabled={isRemoving}
                    aria-label={t(
                      'riskReductionMeasuresLibraryPage.rrmDetail.removeEhsrAria',
                      { ehsr: clauseTitle }
                    )}
                  >
                    {isRemoving ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      <>
                        <Trash2 className='size-4' />
                        {t(
                          'riskReductionMeasuresLibraryPage.rrmDetail.removeEhsrAction'
                        )}
                      </>
                    )}
                  </Button>
                </Item>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
